import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subscription, debounceTime, distinctUntilChanged, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { DisbursementCertificationService } from 'src/app/services/api/disbursement/disbursement.service';
import { DepartmentService } from 'src/app/services/api/departments/department.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  DisbursementApprovalTrackEntry,
  DisbursementCertificationModel,
  DisbursementCertificationListParams,
  DisbursementCertificationTeacher,
  DisbursementDocType,
  DisbursementStatusCode,
  DisbursementTeacherProgress,
  DISBURSEMENT_CALC_DOC_TYPES,
  DISBURSEMENT_DOC_LABELS,
  DISBURSEMENT_SCHEDULE_DOC_TYPES,
} from 'src/app/services/models/disbursement/disbursement.model';
import {
  DisbursementStatusAudience,
  DisbursementStatusDisplay,
  disbursementStatusAudienceFor,
  getDisbursementStatusDisplay,
} from 'src/app/services/models/disbursement/disbursement-status-display';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { DepartmentModel } from 'src/app/services/models/department.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';
import {
  buildAcademicTermNameOptions,
  buildAcademicYearOptions,
  currentAcademicTerm,
  findAcademicTerm,
  toBuddhistYear,
} from 'src/utils/academic-term-options';
import { extractBlobErrorMessage } from 'src/utils/blob-error';
import {
  DISBURSEMENT_CERTIFY_ROLE_IDS,
  DISBURSEMENT_CERTIFY_TITLE_ROLE_IDS,
  DISBURSEMENT_ROUND_ROLE_IDS,
  DISBURSEMENT_SELF_CERTIFY_ROLE_IDS,
  RoleID,
} from 'src/utils/enum/role.enum';
import {
  DisbursementRevisionDialogComponent,
  DisbursementRevisionDialogResult,
} from '../_shared/disbursement-revision-dialog/disbursement-revision-dialog.component';
import { DisbursementCancellationQueueComponent } from '../disbursement-cancellation-queue/disbursement-cancellation-queue.component';
import { DisbursementRoundQueueComponent } from '../round-queue/round-queue.component';

const PREVIEW_ERROR_FALLBACK = 'ไม่สามารถสร้างตัวอย่างเอกสารได้';

// N9 — อาจารย์ที่ไม่อยู่ในชุดผู้มีสิทธิ์เบิกของใบนี้ (ต่ำกว่าเกณฑ์ขั้นต่ำ / ฿0) ยังมีชื่อบน
// เอกสารตามเดิม แต่ไม่ต้องรับรอง และสายอนุมัติก็ไม่รอ — ปุ่มรับรองจึงไม่แสดงให้ท่านนั้น
const NOT_CLAIMING_TEACHER_NOTE = 'ไม่ต้องรับรอง (ไม่มียอดเบิกในเอกสารนี้)';

// แท็บสถานะของตารางเอกสารรายคน — ตรงกับ chip ที่แถวนั้นแสดงจริง: 'completed' คือ
// สถานะ completed ล้วน ๆ (pending_round ยัง chip ว่ารอดำเนินการ จึงอยู่ใต้ 'pending')
// ส่วน conditions_not_met เห็นได้เฉพาะแท็บ "ทั้งหมด"
type CertificationTab = 'all' | 'pending' | 'completed' | 'revision';

interface CertificationTabOption {
  key: CertificationTab;
  label: string;
}

const CERTIFICATION_TABS: readonly CertificationTabOption[] = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'pending', label: 'รอดำเนินการ' },
  { key: 'completed', label: 'เสร็จสิ้น' },
  { key: 'revision', label: 'ส่งกลับแก้ไข' },
];

// เมื่อพ้น phase 1 (pending_round) สารบรรณคือผู้รับไม้ต่อ — context/reference/GLOSSARY_Disbursement.md §3
const NEXT_ACTOR_BY_STATUS: Partial<Record<DisbursementStatusCode, string>> = {
  pending_round: 'เจ้าหน้าที่สารบรรณ',
};

// แท็บบนสุด เห็นเฉพาะผู้บริหารที่สอนจริง (self-certify roles) — context/decisions/DECISION_RolesPermissions.md (DISBURSEMENT_SELF_CERTIFY_ROLE_IDS)
type TopTab = 'mine' | 'queue' | 'cancellation';

// ADR-BU: phase 1 is อาจารย์ -> การเงิน only — context/decisions/DECISION_DisbursementV20.md.
// The ONLY per-cert edge หัวหน้าสาขา/รองฯ บริหาร/รองฯ วิชาการ keep is the อาจารย์ station of
// their OWN certification (isSelfCertifyRole + isOwnRow), reusing teacher_certify verbatim.
// SIT sprint 2 (N5): the รองวิชาการ revision-review section is GONE from this page — the
// send-back IS a real special-correction request and is reviewed on
// /schedule-submissions-management/schedule-correction-requests-review (same role gate),
// so รองวิชาการ keeps only its own "เอกสารของฉัน" table here.
type PrimaryActionKind =
  | 'teacher_certify'
  | 'finance_certify';

type RevisionActionKind =
  | 'teacher_revision'
  | 'finance_revision';

interface CertificationRow {
  id: number;
  teacherName: string;
  teacherEmail: string;
  departmentName: string;
  academicTermLabel: string;
  statusCode: DisbursementStatusCode;
  statusLabel: string;
  updatedAtLabel: string;
  raw: DisbursementCertificationModel;
}

interface CertDocRow {
  key: string;
  docType: DisbursementDocType;
  label: string;
  teacherUserId: number | null;
}

interface PreviewPanelState {
  loading: boolean;
  error: boolean;
  errorMessage: string | null;
  url: SafeResourceUrl | null;
}

interface CertificationDataSource {
  data: CertificationRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-disbursement-certification',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SharedModule,
    DisbursementRevisionDialogComponent,
    DisbursementCancellationQueueComponent,
    DisbursementRoundQueueComponent,
  ],
  templateUrl: './disbursement-certification.component.html',
  styleUrl: './disbursement-certification.component.scss',
})
export class DisbursementCertificationComponent implements OnInit, OnDestroy {
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<unknown>;
  @ViewChild('pendingActionTemplate', { static: true })
  pendingActionTemplate!: TemplateRef<unknown>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<unknown>;
  @ViewChild('primaryConfirmDialog')
  primaryConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('actionSuccessDialog')
  actionSuccessDialog?: ConfirmDialogComponent;
  @ViewChild('revisionDialog')
  revisionDialog!: DisbursementRevisionDialogComponent;

  filterForm = inject(FormBuilder).group({
    academicTermId: [null as number | null],
    academicTerm: [null as string | null],
    academicYear: [null as string | null],
    departmentId: [null as number | null],
    keywords: [''],
  });

  columns: unknown[] = [];
  datasource = signal<CertificationDataSource>(this.createDataSource([]));
  tableLoading = signal(false);
  actionLoading = signal(false);
  selectedRow = signal<CertificationRow | null>(null);
  detailOpen = signal(false);
  successTitle = signal('');
  successMessage = signal('');
  activeTab = signal<CertificationTab>('pending');
  topTab = signal<TopTab>('mine');
  academicTermNameOptions: string[] = [];
  academicYearOptions: string[] = [];
  departmentOptions: DepartmentModel[] = [];
  // Mirrors the filter form's resolved term so the sibling round queue reloads with it.
  selectedAcademicTermId = signal<number | null>(null);
  selectedDepartmentId = signal<number | null>(null);
  // 86eypuk2w — คำค้นที่ "กดค้นหา/ล้าง" แล้วเท่านั้น (app-filter-search-input ยิง searchSubmit
  // ทั้งสองกรณี ไม่ยิงตอนพิมพ์) ส่งต่อให้คิวรอบส่งเบิก — ผูก form control ตรง ๆ ไม่ได้
  // เพราะค่าจะเปลี่ยนทุกตัวอักษรและคิวจะยิง request รัวตาม
  submittedKeywords = signal('');
  readonly searchLabel = 'ชื่อ - นามสกุล';

  primaryActionKind = signal<PrimaryActionKind | null>(null);
  revisionActionKind = signal<RevisionActionKind | null>(null);

  // v1.2 §4 — one inline preview panel per doc_type, keyed on the string
  // (doc_2/doc_3/doc_4/doc_5/doc_7). Reset whenever the detail modal opens on
  // a different row/closes (see onView/onCloseDetail).
  previewPanels = signal<Partial<Record<string, PreviewPanelState>>>({});

  pageSizeOptions = [10, 20, 50];
  readonly statusTabs = CERTIFICATION_TABS;

  private currentPage = 1;
  private currentLimit = 50;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private academicTerms: AcademicTermYearModel[] = [];
  private readonly subscriptions = new Subscription();
  private readonly sortFieldMap: DatatableSortFieldMap = {
    status: 'status',
    updatedAtLabel: 'updated_at',
    teacherName: 'teacher_name',
    departmentName: 'department',
  };
  private readonly authService = inject(AuthService);
  private readonly departmentService = inject(DepartmentService);
  private readonly disbursementService = inject(DisbursementCertificationService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly toast = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly sanitizer = inject(DomSanitizer);
  // Keeps raw URLs to revoke them; sanitized SafeResourceUrl lives in previewPanels.
  private readonly rawPreviewUrls = new Map<string, string>();

  ngOnInit(): void {
    this.initColumns();
    this.listenFilters();
    this.loadDepartmentOptions();
    this.loadFilterOptions();
    this.maybeAutoOpenFocusedCertification();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.revokeAllPreviews();
  }

  // Per-cert queue serves phase-1 actors only — context/reference/CODEMAP_Disbursement.md §FE (disbursement-certification row)
  get showCertificationQueue(): boolean {
    const roleId = this.authService.getActiveRoleId();
    if (roleId == null) {
      return false;
    }

    return DISBURSEMENT_CERTIFY_ROLE_IDS.includes(roleId);
  }

  // อาจารย์เห็นเฉพาะใบของตัวเองอยู่แล้ว — ไม่มีแถบแท็บ ไม่มีแท็บสถานะ และไม่มีช่องค้นหา
  get isTeacherRole(): boolean {
    return this.authService.getActiveRoleId() === RoleID.TEACHER;
  }

  // หัวหน้าสาขา / รองฯ บริหาร / รองฯ วิชาการ ที่สอนจริงก็มีใบรับรองการเบิกจ่ายของตัวเอง
  // และต้องรับรองเองที่สถานีอาจารย์ — โครงคิวเดิม (showCertificationQueue) ไม่เปลี่ยน
  get isSelfCertifyRole(): boolean {
    const roleId = this.authService.getActiveRoleId();

    return roleId != null && DISBURSEMENT_SELF_CERTIFY_ROLE_IDS.includes(roleId);
  }

  // แยกออกมาจาก showRoundQueue เพราะ showTopTabs ต้องรู้ว่า role นี้มีคิวรอบส่งเบิกไหม
  // ก่อนจะตัดสินใจว่าควรมีแถบแท็บหรือไม่
  get hasRoundQueueAccess(): boolean {
    const roleId = this.authService.getActiveRoleId();

    return roleId != null && DISBURSEMENT_ROUND_ROLE_IDS.includes(roleId);
  }

  // The cancellation gate is a Finance-only correction queue. Admin still acts on
  // certification/round workflows, but does not receive this tab by default.
  get showFinanceCancellationQueue(): boolean {
    return this.authService.getActiveRoleId() === RoleID.FINANCE_OFFICER;
  }

  // แถบแท็บมีได้เฉพาะ role ที่มีคิวรอบส่งเบิกจริง ๆ เท่านั้น — ไม่งั้นแท็บขวาจะว่างเปล่า
  // (รองฯ วิชาการ เป็น self-certify แต่ไม่มีคิวรอบ จึงต้องเหลือแค่ "เอกสารของฉัน" ตาม N4)
  get showTopTabs(): boolean {
    return this.showFinanceCancellationQueue
      || (this.hasRoundQueueAccess && (this.isSelfCertifyRole || this.showCertificationQueue));
  }

  // ป้ายแท็บซ้ายเป็นตัวแยกชั้น role: ผู้บริหารที่สอนจริงเห็นใบของตัวเอง
  // ส่วนการเงิน/แอดมินเห็นเอกสารของทุกคนตามขอบเขต policy
  get docsTabLabel(): string {
    return this.isSelfCertifyRole ? 'เอกสารของฉัน' : 'เอกสารส่งเบิก';
  }

  // การเงิน/แอดมิน อ่านคิวรอบเป็น "เอกสารส่งเบิกฉบับรวม" ตามคำเดียวกับหน้า registry
  // ส่วนผู้บริหารที่สอนจริงยังเรียกว่ารอบส่งเบิกตามเดิม
  get queueTabLabel(): string {
    return this.isSelfCertifyRole ? 'รอบส่งเบิก' : 'เอกสารส่งเบิกฉบับรวม';
  }

  // ชื่อหน้าแยกตาม role เช่นเดียวกับเมนู (menus.data.ts): อาจารย์/หัวหน้าสาขา รับรอง
  // ส่วน role อื่นยังเป็นผู้อนุมัติ
  get pageTitle(): string {
    const roleId = this.authService.getActiveRoleId();

    return roleId != null && DISBURSEMENT_CERTIFY_TITLE_ROLE_IDS.includes(roleId)
      ? 'ตรวจสอบและรับรองเอกสารส่งเบิก'
      : 'ตรวจสอบและอนุมัติเอกสารส่งเบิก';
  }

  get showSearch(): boolean {
    return !this.isTeacherRole;
  }

  get showDepartmentFilter(): boolean {
    return this.showSearch;
  }

  // แท็บ "เอกสารของฉัน" มีตารางเสมอ ส่วนแท็บงานคงพฤติกรรมเดิมของแต่ละ role เป๊ะ
  // (หัวหน้าสาขา / รองฯ บริหาร ไม่มีตาราง phase 1 จึงต้องไม่ยิง request ด้วย
  // ส่วนรองฯ วิชาการ แท็บขวาคือตารางคำขอแก้ไขตารางสอน จึงตกมาที่ showCertificationQueue)
  get showCertificationTable(): boolean {
    if (this.topTab() === 'cancellation') {
      return false;
    }

    // การเงิน/แอดมิน — คิวเดิมทั้งดุ้น เพียงแต่ย้ายไปอยู่หลังแท็บ "เอกสารรายคน"
    // แท็บรอบส่งเบิกไม่มีตาราง จึงไม่ยิง request ด้วย
    if (this.showTopTabs && !this.isSelfCertifyRole) {
      return this.topTab() === 'mine';
    }

    if (!this.isSelfCertifyRole) {
      return this.showCertificationQueue;
    }

    return this.topTab() === 'mine' ? true : this.showCertificationQueue;
  }

  // แท็บ "เอกสารของฉัน" ไม่มี sub-filter — ใบของตัวเองมีไม่กี่ใบและอยู่คนละสถานะกันอยู่แล้ว
  // อาจารย์ก็เห็นเฉพาะใบของตัวเองเหมือนกัน จึงตัดแถบสถานะทิ้งทั้งชุด (86eyp443m shot 1)
  get showStatusSubTabs(): boolean {
    if (!this.showCertificationTable || this.isTeacherRole) {
      return false;
    }

    return !(this.isSelfCertifyRole && this.topTab() === 'mine');
  }

  // ทุก role ที่มีคิวรอบส่งเบิกวันนี้ล้วนมีแถบแท็บ — คิวจึงอยู่บนแท็บขวาเสมอ
  // (เงื่อนไข !showTopTabs กันไว้เผื่อมี role ใหม่ที่มีแต่คิว ไม่มีตาราง)
  get showRoundQueue(): boolean {
    return this.hasRoundQueueAccess
      && this.topTab() !== 'cancellation'
      && (!this.showTopTabs || this.topTab() === 'queue');
  }

  get showCancellationQueue(): boolean {
    return this.showFinanceCancellationQueue && this.topTab() === 'cancellation';
  }

  // A getter, not a cached field: switching role must re-render the badges.
  get statusAudience(): DisbursementStatusAudience {
    return disbursementStatusAudienceFor(this.authService.getActiveRoleId());
  }

  onTabChange(tab: CertificationTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadCertifications(1);
  }

  onTopTabChange(tab: TopTab): void {
    if (this.topTab() === tab) {
      return;
    }

    this.topTab.set(tab);
    this.loadCertifications(1);
  }

  onSearch(): void {
    this.submittedKeywords.set(String(this.filterForm.getRawValue().keywords ?? '').trim());
    this.loadCertifications(1);
  }

  onView(row: CertificationRow): void {
    this.revokeAllPreviews();
    this.selectedRow.set(row);
    this.detailOpen.set(true);
  }

  onCloseDetail(): void {
    if (this.actionLoading()) {
      return;
    }

    this.revokeAllPreviews();
    this.detailOpen.set(false);
    this.selectedRow.set(null);
  }

  // finalize() fires after the success callback, so actionLoading is still true here.
  private closeDetailAfterAction(): void {
    this.revokeAllPreviews();
    this.detailOpen.set(false);
    this.selectedRow.set(null);
  }

  // --- v1.2 §4 two-section documents + inline preview ---

  calcSectionDocRows(row: CertificationRow | null): CertDocRow[] {
    if (row == null) {
      return [];
    }

    return DISBURSEMENT_CALC_DOC_TYPES[row.raw.path].flatMap((docType) => this.docRowsFor(row, docType));
  }

  scheduleSectionDocRows(row: CertificationRow | null): CertDocRow[] {
    if (row == null) {
      return [];
    }

    return DISBURSEMENT_SCHEDULE_DOC_TYPES.flatMap((docType) => this.docRowsFor(row, docType));
  }

  docLabel(docType: string): string {
    return DISBURSEMENT_DOC_LABELS[docType] ?? docType;
  }

  isPreviewExpanded(key: string): boolean {
    return this.previewPanels()[key] != null;
  }

  isPreviewLoading(key: string): boolean {
    return this.previewPanels()[key]?.loading ?? false;
  }

  previewHasError(key: string): boolean {
    return this.previewPanels()[key]?.error ?? false;
  }

  previewErrorMessage(key: string): string | null {
    return this.previewPanels()[key]?.errorMessage ?? null;
  }

  previewUrl(key: string): SafeResourceUrl | null {
    return this.previewPanels()[key]?.url ?? null;
  }

  onTogglePreview(docRow: CertDocRow): void {
    if (this.isPreviewExpanded(docRow.key)) {
      this.collapsePreview(docRow.key);
      return;
    }

    this.expandPreview(docRow);
  }

  private docRowsFor(row: CertificationRow, docType: DisbursementDocType): CertDocRow[] {
    if (row.raw.path !== 'international' || !this.isPerTeacherDoc(docType)) {
      return [this.singleDocRow(docType)];
    }

    const teachers = row.raw.teachers ?? [];
    if (teachers.length === 0) {
      return [this.singleDocRow(docType)];
    }

    return teachers.map((teacher) => ({
      key: `${docType}:${teacher.user_id}`,
      docType,
      label: `${this.docLabel(docType)} — ${teacher.full_name ?? '-'}`,
      teacherUserId: teacher.user_id,
    }));
  }

  private isPerTeacherDoc(docType: DisbursementDocType): boolean {
    return docType === 'doc_3' || docType === 'doc_7';
  }

  private singleDocRow(docType: DisbursementDocType): CertDocRow {
    return { key: docType, docType, label: this.docLabel(docType), teacherUserId: null };
  }

  private expandPreview(docRow: CertDocRow): void {
    const row = this.selectedRow();
    if (row == null) {
      return;
    }

    this.patchPreviewPanels(docRow.key, { loading: true, error: false, errorMessage: null, url: null });
    this.disbursementService.previewDocument(row.id, docRow.docType, docRow.teacherUserId).pipe(take(1)).subscribe({
      next: (blob) => {
        const rawUrl = window.URL.createObjectURL(blob);
        this.rawPreviewUrls.set(docRow.key, rawUrl);
        this.patchPreviewPanels(docRow.key, {
          loading: false,
          error: false,
          errorMessage: null,
          url: this.sanitizer.bypassSecurityTrustResourceUrl(`${rawUrl}#toolbar=0&navpanes=0`),
        });
      },
      error: (err) => {
        console.error(err);
        void extractBlobErrorMessage(err, PREVIEW_ERROR_FALLBACK).then((errorMessage) => {
          this.patchPreviewPanels(docRow.key, { loading: false, error: true, errorMessage, url: null });
        });
      },
    });
  }

  private collapsePreview(key: string): void {
    this.revokePreview(key);
    this.previewPanels.update((panels) => {
      const next = { ...panels };
      delete next[key];
      return next;
    });
  }

  private patchPreviewPanels(key: string, state: PreviewPanelState): void {
    this.previewPanels.update((panels) => ({ ...panels, [key]: state }));
  }

  private revokePreview(key: string): void {
    const rawUrl = this.rawPreviewUrls.get(key);
    if (rawUrl != null) {
      window.URL.revokeObjectURL(rawUrl);
      this.rawPreviewUrls.delete(key);
    }
  }

  private revokeAllPreviews(): void {
    this.rawPreviewUrls.forEach((rawUrl) => window.URL.revokeObjectURL(rawUrl));
    this.rawPreviewUrls.clear();
    this.previewPanels.set({});
  }

  // --- v1.2 §3/§4 approval stepper + "รอดำเนินการ" column ---

  currentStationEntry(row: CertificationRow | null): DisbursementApprovalTrackEntry | null {
    return row?.raw.approval_track.find((entry) => entry.state === 'current') ?? null;
  }

  pendingActionLabel(row: CertificationRow | null): string {
    const current = this.currentStationEntry(row);
    if (current == null) {
      return row != null ? NEXT_ACTOR_BY_STATUS[row.statusCode] ?? '-' : '-';
    }

    const names = current.person_names.length > 0 ? current.person_names.join(', ') : 'ยังไม่ได้กำหนด';
    return `${current.role_label}: ${names}`;
  }

  stationPersonNames(entry: DisbursementApprovalTrackEntry): string {
    return entry.person_names.length > 0 ? entry.person_names.join(', ') : 'ยังไม่ได้กำหนด';
  }

  ownerLabel(cert: DisbursementCertificationModel | null): string {
    if (cert == null) {
      return '-';
    }

    if (cert.user != null) {
      return cert.user.full_name;
    }

    const subject = cert.subject;
    if (subject != null) {
      return `${subject.code} ${subject.name}`.trim();
    }

    return '-';
  }

  isInternational(row: CertificationRow | null): boolean {
    return row?.raw.path === 'international';
  }

  teacherListLabel(row: CertificationRow | null): string {
    const teachers = row?.raw.teachers ?? [];
    const names = teachers
      .map((teacher) => teacher.full_name)
      .filter((name): name is string => name != null && name !== '');
    return names.length > 0 ? names.join(', ') : '-';
  }

  // ADR-BK: ฿0 co-teachers stay on the document but owe no รับรอง — context/decisions/DECISION_DisbursementV13.md
  teacherProgress(entry: DisbursementApprovalTrackEntry): DisbursementTeacherProgress[] {
    return (entry.teacher_progress ?? []).filter((member) => member.required !== false);
  }

  hasTeacherProgress(entry: DisbursementApprovalTrackEntry): boolean {
    return this.teacherProgress(entry).length > 0;
  }

  teacherProgressCountLabel(entry: DisbursementApprovalTrackEntry): string {
    const members = this.teacherProgress(entry);
    if (members.length === 0) {
      return '';
    }

    const certified = members.filter((member) => member.certified_at != null).length;
    return `(${certified}/${members.length})`;
  }

  private currentUserId(): number | null {
    return this.authService.currentUser()?.user.id ?? null;
  }

  // เทียบ id ตรง ๆ ไม่ใช่ role — ผู้บริหารที่สอนจริงได้ปุ่มเฉพาะบนใบของตัวเองเท่านั้น
  // (ใบต่อรายวิชาของภาคนานาชาติเจ้าของอยู่ใน teachers[] ไม่ใช่ user)
  private isOwnRow(row: CertificationRow | null): boolean {
    const userId = this.currentUserId();
    if (row == null || userId == null) {
      return false;
    }

    if (row.raw.user?.id === userId) {
      return true;
    }

    return (row.raw.teachers ?? []).some((teacher) => teacher.user_id === userId);
  }

  private currentTeacherEntry(row: CertificationRow | null): DisbursementCertificationTeacher | undefined {
    if (row == null) {
      return undefined;
    }

    if (row.raw.path !== 'international') {
      return undefined;
    }

    const userId = this.currentUserId();
    if (userId == null) {
      return undefined;
    }

    return (row.raw.teachers ?? []).find((teacher) => teacher.user_id === userId);
  }

  isWaitingOnOtherTeachers(row: CertificationRow | null): boolean {
    if (row == null) {
      return false;
    }

    if (row.statusCode !== 'pending_teacher') {
      return false;
    }

    return this.currentTeacherEntry(row)?.certified_at != null;
  }

  // N9 — `required` มาจาก DisbursementCertification#claiming_teacher_ids ฝั่ง API
  // ใบที่ไม่มี teachers[] (ภาคปกติ / เบิกรายอาจารย์) คืน undefined = ต้องรับรองตามปกติ
  isClaimingTeacherForRow(row: CertificationRow | null): boolean {
    return this.currentTeacherEntry(row)?.required !== false;
  }

  // ข้อความเงียบ ๆ แทนปุ่มรับรอง ใช้ถ้อยคำเดียวกับ sub-row ของ stepper
  teacherNotClaimingLabel(row: CertificationRow | null): string {
    if (row?.statusCode !== 'pending_teacher') {
      return '';
    }

    if (!this.isTeacherStationActor(row) || this.isClaimingTeacherForRow(row)) {
      return '';
    }

    return NOT_CLAIMING_TEACHER_NOTE;
  }

  private isTeacherStationActor(row: CertificationRow | null): boolean {
    if (this.isTeacherRole) {
      return true;
    }

    return this.isSelfCertifyRole && this.isOwnRow(row);
  }

  teacherWaitingLabel(row: CertificationRow | null): string {
    if (!this.isWaitingOnOtherTeachers(row)) {
      return '';
    }

    const teacherEntry = row?.raw.approval_track.find((entry) => entry.station === 'teacher');
    const count = teacherEntry != null ? this.teacherProgressCountLabel(teacherEntry) : '';
    return `รออาจารย์ท่านอื่นรับรอง ${count}`.trim();
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadCertifications(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadCertifications(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadCertifications(1);
  }

  // --- Primary action (certify / approve / approve-revision) ---

  canActPrimary(row: CertificationRow | null): boolean {
    return this.resolvePrimaryActionKind(row) != null;
  }

  primaryActionButtonLabel(row: CertificationRow | null): string {
    return this.primaryActionButtonLabelForKind(this.resolvePrimaryActionKind(row));
  }

  onOpenPrimaryConfirm(): void {
    const kind = this.resolvePrimaryActionKind(this.selectedRow());
    if (kind == null) {
      return;
    }

    this.primaryActionKind.set(kind);
    this.primaryConfirmDialog.open();
  }

  onConfirmPrimaryAction(): void {
    const row = this.selectedRow();
    const kind = this.primaryActionKind();
    if (row == null || kind == null) {
      return;
    }

    this.actionLoading.set(true);
    this.resolvePrimaryActionCall(kind, row.id)
      .pipe(finalize(() => this.actionLoading.set(false)))
      .subscribe({
        next: () => {
          this.primaryConfirmDialog.close();
          this.closeDetailAfterAction();
          this.loadCertifications(this.currentPage);
          this.showSuccess(this.primaryActionSuccessTitle(kind), this.primaryActionSuccessMessage(kind));
        },
        error: (err) => {
          console.error(err);
          this.toast.toastError(this.extractErrorMessage(err));
        },
      });
  }

  primaryActionDialogTitle(): string {
    switch (this.primaryActionKind()) {
      case 'finance_certify':
        return 'ยืนยันการเห็นชอบเอกสาร';
      case 'teacher_certify':
      case null:
      default:
        return 'ยืนยันการรับรองเอกสาร';
    }
  }

  // confirm-dialog renders `message` with white-space: pre-line, so the finance-only
  // second line rides a \n rather than needing a second input.
  primaryActionDialogMessage(): string {
    switch (this.primaryActionKind()) {
      case 'finance_certify':
        return 'ต้องการเห็นชอบเอกสารการเบิกจ่ายใช่หรือไม่?\nเมื่อเห็นชอบแล้ว เอกสารจะส่งต่อให้เจ้าหน้าที่สารบรรณดำเนินการต่อในขั้นตอนถัดไป';
      case 'teacher_certify':
      case null:
      default:
        return 'ต้องการรับรองเอกสารการเบิกจ่ายใช่หรือไม่?';
    }
  }

  primaryActionDialogConfirmText(): string {
    return this.primaryActionButtonLabelForKind(this.primaryActionKind());
  }

  // --- Revision / send-back action ---

  canShowRevisionButton(row: CertificationRow | null): boolean {
    return this.isRevisionActorForRow(row);
  }

  canOpenRevision(row: CertificationRow | null): boolean {
    return this.resolveRevisionActionKind(row) != null;
  }

  revisionWindowLabel(row: CertificationRow | null): string {
    if (row == null || !this.canOpenRevision(row)) {
      return '';
    }

    const deadline = row.raw.revision_deadline;
    if (deadline == null) {
      return '';
    }

    return `สามารถส่งกลับแก้ไขได้ถึงวันที่ ${this.formatDate(deadline)}`;
  }

  revisionButtonLabel(): string {
    return 'ส่งกลับแก้ไข';
  }

  revisionDisabledTooltip(row: CertificationRow | null): string {
    if (row == null) {
      return '';
    }

    const deadline = row.raw.revision_deadline;
    if (deadline != null) {
      return `พ้นกำหนดการแก้ไข (เกินกำหนดวันที่ ${this.formatDate(deadline)}) — รับรองได้เท่านั้น`;
    }

    return 'พ้นกำหนดการแก้ไข — รับรองได้เท่านั้น';
  }

  // Redesign: send-back needs the subject picker before the dialog can open, so this
  // fetches GET revision_candidates first and only opens the dialog on success — the
  // dialog's own candidates/showPicker are set directly on the ViewChild instance (see
  // DisbursementRevisionDialogComponent's own comment on why not a template binding).
  onOpenRevisionDialog(): void {
    const row = this.selectedRow();
    const kind = this.resolveRevisionActionKind(row);
    if (row == null || kind == null) {
      return;
    }

    this.revisionActionKind.set(kind);
    this.actionLoading.set(true);
    this.disbursementService.getRevisionCandidates(row.id)
      .pipe(finalize(() => this.actionLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.revisionDialog.candidates = response.candidates;
          this.revisionDialog.showPicker = response.show_picker;
          this.revisionDialog.open();
        },
        error: (err) => {
          console.error(err);
          this.toast.toastError(this.extractErrorMessage(err));
        },
      });
  }

  onRevisionCancelled(): void {
  }

  onRevisionSubmitted(result: DisbursementRevisionDialogResult): void {
    const row = this.selectedRow();
    const kind = this.revisionActionKind();
    if (row == null || kind == null) {
      return;
    }

    this.actionLoading.set(true);
    this.buildRevisionRequest(kind, row.id, result)
      .pipe(finalize(() => this.actionLoading.set(false)))
      .subscribe({
        next: () => {
          this.revisionDialog.close();
          this.closeDetailAfterAction();
          this.loadCertifications(this.currentPage);
          this.showSuccess('ส่งคำขอแก้ไขสำเร็จ', this.revisionSuccessMessage());
        },
        error: (err) => {
          console.error(err);
          this.toast.toastError(this.extractErrorMessage(err));
        },
      });
  }

  revisionDialogTitle(): string {
    return 'ส่งกลับแก้ไข';
  }

  revisionDialogConfirmText(): string {
    return 'ยืนยันส่งกลับแก้ไข';
  }

  // --- Display helpers ---

  statusDisplay(status: DisbursementStatusCode): DisbursementStatusDisplay {
    return getDisbursementStatusDisplay(status, this.statusAudience);
  }

  isWindowClosed(row: CertificationRow | null): boolean {
    return row?.raw.window_closed === true;
  }

  formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return [
      String(date.getDate()).padStart(2, '0'),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(toBuddhistYear(date.getFullYear())),
    ].join('/');
  }

  private initColumns(): void {
    this.columns = [
      // Declared rather than left to the shared datatable's injected "#" column — same
      // prop, so normalizeColumns() reuses this one and the row numbering still fills in.
      {
        prop: 'order',
        name: 'ลำดับ',
        sortable: false,
        draggable: false,
        resizeable: false,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
        cellClass: 'text-center',
        headerClass: 'd-flex justify-content-center',
      },
      {
        prop: 'teacherName',
        name: 'ชื่อ - นามสกุล',
        sortable: true,
        width: 190,
        minWidth: 170,
      },
      {
        prop: 'departmentName',
        name: 'สาขาวิชา',
        sortable: true,
        width: 170,
        minWidth: 150,
      },
      {
        prop: 'status',
        name: 'สถานะ',
        sortable: true,
        cellTemplate: this.statusTemplate,
        width: 180,
        minWidth: 170,
      },
      {
        prop: 'pendingAction',
        // Renamed off 'รอดำเนินการ': the summary badge now carries that exact word,
        // and a column repeating it would read as a duplicate of the status pill.
        name: 'ผู้ที่ต้องดำเนินการ',
        sortable: false,
        cellTemplate: this.pendingActionTemplate,
        width: 220,
        minWidth: 180,
      },
      {
        prop: 'updatedAtLabel',
        name: 'อัปเดตล่าสุด',
        sortable: true,
        width: 130,
        minWidth: 120,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center disb-cert-action-cell',
        headerClass: 'text-center disb-cert-action-header',
      },
    ];
  }

  private listenFilters(): void {
    this.subscriptions.add(
      this.filterForm.valueChanges
        .pipe(
          debounceTime(250),
          distinctUntilChanged(),
        )
        .subscribe(() => this.loadCertifications(1)),
    );
    this.subscriptions.add(
      this.filterForm.controls.academicYear.valueChanges.subscribe(() => this.onAcademicYearChanged()),
    );
    this.subscriptions.add(
      this.filterForm.controls.academicTerm.valueChanges.subscribe(() => this.syncAcademicTermId()),
    );
    this.subscriptions.add(
      this.filterForm.controls.departmentId.valueChanges.subscribe((departmentId) => {
        this.selectedDepartmentId.set(departmentId ?? null);
      }),
    );
  }

  private onAcademicYearChanged(): void {
    const { academicYear, academicTerm } = this.filterForm.getRawValue();
    this.academicTermNameOptions = buildAcademicTermNameOptions(this.academicTerms, academicYear);
    const nextTerm = academicTerm != null && this.academicTermNameOptions.includes(academicTerm)
      ? academicTerm
      : this.academicTermNameOptions[0] ?? null;
    this.filterForm.patchValue({ academicTerm: nextTerm }, { emitEvent: false });
    this.syncAcademicTermId();
  }

  private syncAcademicTermId(): void {
    const { academicTerm, academicYear } = this.filterForm.getRawValue();
    const resolved = findAcademicTerm(this.academicTerms, academicTerm, academicYear);
    this.filterForm.patchValue({ academicTermId: resolved?.id ?? null }, { emitEvent: false });
    this.selectedAcademicTermId.set(resolved?.id ?? null);
  }

  private loadFilterOptions(): void {
    this.studyPeriodService.getAcademicTermYears().pipe(take(1)).subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicYearOptions = buildAcademicYearOptions(terms);
        const defaultTerm = currentAcademicTerm(terms, this.authService.currentUser()?.academic_term);
        const defaultYear = defaultTerm != null ? String(toBuddhistYear(defaultTerm.year)) : null;
        this.academicTermNameOptions = buildAcademicTermNameOptions(terms, defaultYear);
        this.filterForm.patchValue(
          {
            academicYear: defaultYear,
            academicTerm: defaultTerm != null ? String(defaultTerm.name).trim() : null,
            academicTermId: defaultTerm?.id ?? null,
          },
          { emitEvent: false },
        );
        this.selectedAcademicTermId.set(defaultTerm?.id ?? null);
        this.loadCertifications(1);
      },
      error: (err) => {
        console.error(err);
        this.loadCertifications(1);
      },
    });
  }

  private loadDepartmentOptions(): void {
    this.departmentService.getDepartments({ limit: 200 }).pipe(take(1)).subscribe({
      next: (response) => {
        this.departmentOptions = response.data;
      },
      error: (err) => console.error(err),
    });
  }

  private maybeAutoOpenFocusedCertification(): void {
    const raw = this.route.snapshot.queryParamMap.get('certification_id');
    if (raw == null || raw.trim() === '') {
      return;
    }

    const certificationId = Number(raw);
    if (!Number.isFinite(certificationId) || certificationId <= 0) {
      return;
    }

    this.disbursementService.getCertification(certificationId).pipe(take(1)).subscribe({
      next: (cert) => {
        this.onView(this.mapCertificationToRow(cert));
      },
      error: () => {
        // Out of scope or missing — silent fallback to list.
      },
    });
  }

  private showSuccess(title: string, message: string): void {
    this.successTitle.set(title);
    this.successMessage.set(message);
    this.actionSuccessDialog?.open();
  }

  private loadCertifications(page = this.currentPage): void {
    this.currentPage = page;

    // แท็บงานของหัวหน้าสาขา / รองฯ บริหาร ไม่มีตาราง phase 1 — ต้องไม่ยิง request ทิ้ง
    if (!this.showCertificationTable) {
      this.datasource.set(this.createDataSource([], page, this.currentLimit));
      this.tableLoading.set(false);
      return;
    }

    this.tableLoading.set(true);
    this.disbursementService.getCertifications({
      ...this.buildFilterParams(),
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort as DisbursementCertificationListParams['sort'],
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        this.datasource.set(this.createDataSource(
          response.data.map((cert) => this.mapCertificationToRow(cert)),
          response.page,
          response.limit,
          response.total,
          response.total_pages,
        ));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private buildFilterParams(): DisbursementCertificationListParams {
    const { academicTermId, departmentId, keywords } = this.filterForm.getRawValue();
    const params: DisbursementCertificationListParams = {};

    if (academicTermId != null) {
      params.academic_term_id = academicTermId;
    }

    if (departmentId != null) {
      params.department_id = departmentId;
    }

    const normalizedKeywords = String(keywords ?? '').trim();
    if (normalizedKeywords !== '') {
      params.keywords = normalizedKeywords;
    }

    const onOwnTab = this.isSelfCertifyRole && this.topTab() === 'mine';

    if (this.isSelfCertifyRole) {
      params.ownership = onOwnTab ? 'mine' : 'others';
    }

    // กรองสถานะได้ก็ต่อเมื่อแถบสถานะยังแสดงอยู่ — ไม่งั้นตารางจะค้างอยู่ที่ activeTab
    // ค่าเริ่มต้น ('รอดำเนินการ') โดยที่ผู้ใช้ไม่มีปุ่มไหนให้ขยายกลับเป็นทั้งหมด
    if (this.showStatusSubTabs) {
      this.applyStatusTabFilter(params);
    }

    return params;
  }

  private applyStatusTabFilter(params: DisbursementCertificationListParams): void {
    switch (this.activeTab()) {
      case 'completed':
        params.status = 'completed';
        return;
      case 'revision':
        params.status = 'revision_schedule_requested';
        return;
      case 'pending': {
        const status = this.pendingStatusForRole();
        if (status != null) {
          params.status = status;
        } else {
          // role ที่ไม่ได้ยืนสถานีใดสถานีหนึ่ง (แอดมิน/สารบรรณ/หัวหน้าสาขา) — ต้องกรองเป็นชุด
          // สถานะที่ยังเดินอยู่ ไม่งั้นแท็บนี้จะให้ผลเท่ากับ "ทั้งหมด"
          params.scope = 'pending';
        }
        return;
      }
      case 'all':
        return;
    }
  }

  private pendingStatusForRole(): DisbursementStatusCode | null {
    const roleId = this.authService.getActiveRoleId();

    if (roleId === RoleID.TEACHER) {
      return 'pending_teacher';
    }

    if (roleId === RoleID.FINANCE_OFFICER) {
      return 'pending_finance';
    }

    return null;
  }

  private resolvePrimaryActionKind(row: CertificationRow | null): PrimaryActionKind | null {
    if (row == null || this.isWindowClosed(row)) {
      return null;
    }

    const roleId = this.authService.getActiveRoleId();

    if (roleId === RoleID.TEACHER && row.statusCode === 'pending_teacher') {
      // ADR-W: intl cert teacher station requires each member to certify independently — context/decisions/DECISION_DisbursementV13.md
      // ADR-BK: a member outside the claiming set owes no รับรอง at all — context/decisions/DECISION_DisbursementV13.md
      if (!this.isClaimingTeacherForRow(row)) {
        return null;
      }

      return this.isWaitingOnOtherTeachers(row) ? null : 'teacher_certify';
    }

    // ผู้บริหารที่สอนจริงยืนที่สถานีอาจารย์ของใบตัวเอง — ใช้เอดจ์เดียวกับอาจารย์ทุกอย่าง
    if (this.isSelfCertifyRole && this.isOwnRow(row) && row.statusCode === 'pending_teacher') {
      if (!this.isClaimingTeacherForRow(row)) {
        return null;
      }

      return this.isWaitingOnOtherTeachers(row) ? null : 'teacher_certify';
    }

    if (roleId === RoleID.FINANCE_OFFICER && row.statusCode === 'pending_finance') {
      return 'finance_certify';
    }

    return null;
  }

  private resolvePrimaryActionCall(kind: PrimaryActionKind, id: number): Observable<DisbursementCertificationModel> {
    switch (kind) {
      case 'teacher_certify':
        return this.disbursementService.teacherCertify(id);
      case 'finance_certify':
        return this.disbursementService.financeCertify(id);
    }
  }

  private primaryActionSuccessTitle(kind: PrimaryActionKind): string {
    switch (kind) {
      case 'finance_certify':
        return 'เห็นชอบใบรับรองการเบิกจ่ายสำเร็จ';
      case 'teacher_certify':
      default:
        return 'รับรองใบรับรองการเบิกจ่ายสำเร็จ';
    }
  }

  private primaryActionSuccessMessage(kind: PrimaryActionKind): string {
    switch (kind) {
      case 'finance_certify':
        return 'เห็นชอบใบรับรองการเบิกจ่ายเรียบร้อยแล้ว รอเจ้าหน้าที่สารบรรณจัดรอบส่งเบิก';
      case 'teacher_certify':
      default:
        return 'รับรองใบรับรองการเบิกจ่ายเรียบร้อยแล้ว';
    }
  }

  private primaryActionButtonLabelForKind(kind: PrimaryActionKind | null): string {
    switch (kind) {
      case 'finance_certify':
        return 'เห็นชอบ';
      case 'teacher_certify':
      case null:
      default:
        return 'รับรอง';
    }
  }

  private isRevisionActorForRow(row: CertificationRow | null): boolean {
    if (row == null || this.isWindowClosed(row)) {
      return false;
    }

    const roleId = this.authService.getActiveRoleId();

    if (roleId === RoleID.TEACHER) {
      // A member who already stamped their own entry has spent their turn: only the
      // co-teachers the cert still waits for — or the next station — may send it back.
      return row.statusCode === 'pending_teacher' && !this.isWaitingOnOtherTeachers(row);
    }

    if (this.isSelfCertifyRole && this.isOwnRow(row)) {
      return row.statusCode === 'pending_teacher' && !this.isWaitingOnOtherTeachers(row);
    }

    if (roleId === RoleID.FINANCE_OFFICER) {
      return row.statusCode === 'pending_finance';
    }

    return false;
  }

  private resolveRevisionActionKind(row: CertificationRow | null): RevisionActionKind | null {
    if (!this.isRevisionActorForRow(row) || row == null) {
      return null;
    }

    const roleId = this.authService.getActiveRoleId();

    if (roleId === RoleID.TEACHER) {
      return row.raw.revision_allowed ? 'teacher_revision' : null;
    }

    if (this.isSelfCertifyRole && this.isOwnRow(row)) {
      return row.raw.revision_allowed ? 'teacher_revision' : null;
    }

    if (roleId === RoleID.FINANCE_OFFICER) {
      return row.raw.revision_allowed ? 'finance_revision' : null;
    }

    return null;
  }

  private buildRevisionRequest(
    kind: RevisionActionKind,
    id: number,
    result: DisbursementRevisionDialogResult,
  ): Observable<DisbursementCertificationModel> {
    switch (kind) {
      case 'teacher_revision':
      case 'finance_revision':
        return this.disbursementService.requestRevision(id, {
          comment: result.reason,
          items: result.items,
          new_course_request_note: result.new_course_request_note,
        });
    }
  }

  private revisionSuccessMessage(): string {
    return 'ส่งคำขอแก้ไขตารางสอนไปยังรองวิชาการเรียบร้อยแล้ว';
  }

  private mapCertificationToRow(cert: DisbursementCertificationModel): CertificationRow {
    return {
      id: cert.id,
      teacherName: this.ownerLabel(cert),
      teacherEmail: cert.user?.email ?? '-',
      departmentName: cert.department.name_th ?? cert.department.name ?? cert.department.name_en ?? '-',
      academicTermLabel: `${cert.academic_term.name}/${toBuddhistYear(cert.academic_term.year)}`,
      statusCode: cert.status_code,
      // Derived from the locked §6 enum map (via the display mapper) rather than
      // trusting the server's `status_label` string verbatim, so the Thai copy is
      // stable regardless of backend wording — and carries the same audience the
      // badge renders with.
      statusLabel: getDisbursementStatusDisplay(cert.status_code, this.statusAudience).label,
      updatedAtLabel: this.formatDate(cert.updated_at),
      raw: cert,
    };
  }

  private createDataSource(
    rows: CertificationRow[],
    page = 1,
    limit = 50,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
  ): CertificationDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private extractErrorMessage(err: unknown): string {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object' && 'errors' in error) {
        const errors = (error as { errors?: unknown }).errors;
        if (Array.isArray(errors) && errors.length > 0) {
          return String(errors[0]);
        }
        if (typeof errors === 'string') {
          return errors;
        }
      }
    }

    return 'ไม่สามารถดำเนินการได้';
  }
}
